import { ReactElement, useEffect, useState } from 'react';
import { ApiRoute, FeatureFlag, Route } from 'config';
import { daysElapsed } from 'utils/dateTimes';

import useNotifications from 'hooks/useNotifications';
import useUpcomingMenus from 'hooks/useUpcomingMenus';
import useCurrentUser from 'hooks/useCurrentUser';
import FetchState from 'models/FetchState';

import { Component as WarningIcon } from 'icons/icon-circle--warning.svg';

import Banner from 'components/shared/Banner';
import AddressBanner from 'components/shared/banners/AddressBanner';
import ReactivationBanner from 'components/shared/banners/ReactivationBanner';
import GiftUserPaymentBanner from 'components/shared/banners/GiftUserPaymentBanner';
import SkeletonLine from 'components/shared/skeletons/SkeletonLine';

import LessPrepBanner from './banners/LessPrepBanner';
import UpcomingDeliveriesWidget from './UpcomingMenu/UpcomingDeliveriesWidget';
import ThanksgivingBanner from './banners/ThanksgivingBanner';
import { CartProvider } from '../../contexts/Cart';
import CartModel, { type ICart } from '../../models/Cart';
import { get } from '../../utils/network';
import useAsyncEffect from '../../hooks/useAsyncEffect';
import GiveawaysBanner from './banners/GiveawaysBanner';

type Props = {
    nextSkipDeadline: string;
    isLtoMenuSkipped: boolean;
};

function Header({ nextSkipDeadline, isLtoMenuSkipped }: Props): ReactElement {
  const { upcomingMenus } = useUpcomingMenus();
  const {
    currentUser,
    fetchState: userFetchState,
  } = useCurrentUser();

  const {
    notifications,
    fetchState: notificationsFetchState,
  } = useNotifications();

  const {
    isDeactivated,
    display_name: displayName,
    registered_at: registeredAt,
    credit,
    payment_method: paymentMethod,
  } = currentUser;
  const isDiversifiedEnabled = currentUser.hasFeatureEnabled(FeatureFlag.DIVERSIFIED_PLANS);
  const isLTOThanksgivingBannerEnabled = currentUser
    .hasFeatureEnabled(FeatureFlag.LTO_THANKSGIVING_DASHBOARD_BANNER);

  const {
    addressChange,
    lessPrepAnnouncement,
  } = notifications;

  const isSabEnabled = currentUser.hasFeatureEnabled(FeatureFlag.SAB_BANNER);
  const thanksgivingMenu = upcomingMenus
    .find((menu) => menu.is_thanksgiving_menu);
  const ltoCartId = thanksgivingMenu?.cart_id;
  const [ltoCart, setLtoCart] = useState<CartModel | undefined>(undefined);
  const [unskippingLtoMenu, setUnskippingLtoMenu] = useState(false);
  const [isLtoMenuUnskipped, setIsLtoMenuUnskipped] = useState(false);
  const [isReactivatedUser, setIsReactivatedUser] = useState(false);
  useAsyncEffect(async () => {
    if (ltoCartId) {
      const response = await get<ICart>(`${ApiRoute.CARTS}/${ltoCartId}`);
      const { parsedBody, ok } = response;
      if (ok && parsedBody) {
        setLtoCart(new CartModel(parsedBody));
        if (unskippingLtoMenu) {
          // LTO cart is now reloaded so we can consider the unskip process complete
          setUnskippingLtoMenu(false);
          setIsLtoMenuUnskipped(true);
        }
      }
    }
  }, [ltoCartId, unskippingLtoMenu, isReactivatedUser]);

  interface StatusMessages {
        message?: string;
    }

  const [statusMessages, setStatusMessages] = useState<StatusMessages | null>(null);

  const currentWeekMenuCartPrice = upcomingMenus.filter((item) => (
    item.current_week_menu
  ))[0]?.subtotalPrice;

  useEffect(() => {
    if (notifications?.statusMessages !== undefined) {
      setStatusMessages(notifications?.statusMessages);
    }
  }, [notifications]);

  const isMultipleBanners = (): boolean => {
    // multiple banners styling does not apply if diversified ff is of
    if (!isDiversifiedEnabled) return false;

    return (currentWeekMenuCartPrice > credit && paymentMethod === null)
            || isDeactivated || statusMessages !== undefined
            || lessPrepAnnouncement !== undefined || addressChange !== undefined;
  };

  return (
    <>
      {(userFetchState === FetchState.SUCCESS
                || userFetchState === FetchState.RELOADING) && (
                <div className="c-orders-header">
                    {registeredAt && daysElapsed(registeredAt) < 15 && !isDiversifiedEnabled && (
                    <p className="c-heading c-heading--brand-2">
                      {'Hi there '}
                      <span data-mf-replace-inner="**FILTERED**">
                        {displayName}
                      </span>
                    </p>
                    )}

                  <h1 className="c-heading c-heading--brand-4">
                    Upcoming Deliveries
                  </h1>

                    {isLTOThanksgivingBannerEnabled && (
                    <>
                      { ltoCart && (!isLtoMenuSkipped || isLtoMenuUnskipped
                          || isReactivatedUser) ? (
                            <CartProvider value={ltoCart}>
                              <ThanksgivingBanner
                                hasCart
                                isLtoMenuUnskipped={isLtoMenuUnskipped}
                                isReactivatedUser={isReactivatedUser}
                              />
                            </CartProvider>
                        ) : (
                          <ThanksgivingBanner
                            isLtoMenuUnskipped={isLtoMenuUnskipped}
                            setUnskippingLtoMenu={setUnskippingLtoMenu}
                            isLtoMenuSkipped={isLtoMenuSkipped}
                            skippedCartId={ltoCartId}
                            setIsReactivatedUser={setIsReactivatedUser}
                          />
                        )}
                    </>
                    )}

                    {!isDiversifiedEnabled && (
                    <p>
                      Choose from 70+ weekly options—you can add any

                      <span className="u-text--bold"> meal kit, ready to eat, or grocery </span>
                      items
                      to your order!
                    </p>
                    )}
                </div>
      )}

      {userFetchState === FetchState.IN_PROGRESS && (
        <>
          <div className="c-orders-header">
            <h1 className="c-heading c-heading--brand-4">
              <SkeletonLine width={320} height={35} />
            </h1>

            <p>
              <SkeletonLine widthp={50} height={16} />
            </p>
          </div>
          <div className="c-order c-order--skeleton c-order-header">
            <div className="c-order__content">
              <div className="c-skeleton-circle" />
              <div>
                <SkeletonLine width={180} className="d-block d-sm-none" />
                <SkeletonLine width={120} className="d-none d-lg-block" />
                <SkeletonLine width={180} />
              </div>
            </div>
            <div className="c-order__content">
              <div className="c-skeleton-circle" />
              <div>
                <SkeletonLine width={180} className="d-block d-sm-none" />
                <SkeletonLine width={120} className="d-none d-lg-block" />
                <SkeletonLine width={180} />
              </div>
            </div>
            <div className="c-order__content" style={{ marginLeft: '1.5rem' }}>
              <div className="c-skeleton-circle" />
              <div>
                <SkeletonLine width={180} className="d-block d-sm-none" />
                <SkeletonLine width={120} className="d-none d-lg-block" />
                <SkeletonLine width={180} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Banners */}
      <div className={`c-orders-banners ${isMultipleBanners() ? 'c-orders-banners--multiple-banners' : ''}`}>
        {isDiversifiedEnabled && (
        <div
          className={`c-orders-widget ${isMultipleBanners() ? 'c-orders-widget--multiple-banners' : ''}`}
        >
          <UpcomingDeliveriesWidget nextSkipDeadline={nextSkipDeadline} />
        </div>
        )}

        {currentWeekMenuCartPrice > credit && paymentMethod === null && (
        <GiftUserPaymentBanner
          centered={false}
          onClick={() => {
            window.location.href = Route.PROFILE_EDIT_PAYMENT;
          }}
        />
        )}
        {(notificationsFetchState === FetchState.SUCCESS
                    || notificationsFetchState === FetchState.RELOADING) && (
                    <>

                        {lessPrepAnnouncement && <LessPrepBanner id={lessPrepAnnouncement.id} />}
                        {isDeactivated && <ReactivationBanner />}
                        {addressChange && <AddressBanner />}
                        {isSabEnabled && <GiveawaysBanner />}
                    </>
        )}
        {statusMessages && statusMessages?.message && (
        <Banner icon={WarningIcon}>
          <div dangerouslySetInnerHTML={{ __html: statusMessages.message }} />
        </Banner>
        )}

      </div>
    </>
  );
}

export default Header;
